import React, { useEffect, useState, useMemo } from "react";
import { Html, useGLTF } from "@react-three/drei";
import "./Main.scss";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import i18n from "i18n/config";
import { useTranslation } from "react-i18next";
import { MathUtils } from "three";
import { mapLinear } from "three/src/math/MathUtils";

interface MainProps {
  setScene: React.Dispatch<React.SetStateAction<string>>;
  setSuspenseLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onIntroComplete?: () => void;
  skipIntroRef?: React.MutableRefObject<(() => void) | null>;
  replayIntroRef?: React.MutableRefObject<(() => void) | null>;
  startIntro?: boolean;
}

// Persists across unmount/remount within the same page load so the intro
// does not replay when the user navigates to a sub-scene and returns.
let introHasPlayed = false;

const sceneKeys = {
  Object_1_scene: "LakeBearbine",
  Object_2_scene: "HearingRoom",
  Object_3_scene: "HeartGarden",
  Object_4_scene: "ParliamentHill",
  Object_5_scene: "Classroom",
};

const ASSET_PATHS = {
  background: "assets/models/backgrounds/set_main_menu_v002_optimized.glb",
  characterSb: "assets/models/characters/spirit_bear_rig_low.gltf",
  animationSb: "assets/animations/MainMenu/main_menu_idle_01B_spirit_bear.gltf",
  animationSbIntro:
    "assets/animations/MainMenu/main_menu_intro_01B_spirit_bear.gltf",
  characterMary: "assets/models/characters/mary_bear_optimized.glb",
  animationMary: "assets/animations/MainMenu/main_menu_idle_01B_mary_bear.gltf",
  animationMaryIntro:
    "assets/animations/MainMenu/main_menu_intro_01B_mary_bear.gltf",
};

// Preload all assets
useGLTF.preload(ASSET_PATHS.background);
useGLTF.preload(ASSET_PATHS.characterSb);
useGLTF.preload(ASSET_PATHS.animationSb);
useGLTF.preload(ASSET_PATHS.animationSbIntro);
useGLTF.preload(ASSET_PATHS.characterMary);
useGLTF.preload(ASSET_PATHS.animationMary);
useGLTF.preload(ASSET_PATHS.animationMaryIntro);

function Main({
  setScene,
  setSuspenseLoading,
  onIntroComplete,
  skipIntroRef,
  replayIntroRef,
  startIntro = true,
}: MainProps) {
  // Seconds into the intro audio at which the camera starts moving to Camera001.
  // Adjust this to match the audio cue in Intro_1A_MapTitleScreen.mp3.
  const INTRO_CAM_TRANSITION_AT = 41;
  const LIPSYNC_FPS = 12;

  const lang = i18n.language;
  const { t } = useTranslation();

  const [isReady, setIsReady] = useState(false);

  // "preIntro": waiting for language select (idle anim, cam at cam1)
  // "intro":    intro animation + audio playing
  // "idle":     looping idle after intro (or on return visits)
  const [phase, setPhase] = useState<"preIntro" | "intro" | "idle">(() =>
    introHasPlayed ? "idle" : "preIntro",
  );

  // Load all assets at once (no conditional loading to avoid useGLTF issues)
  const gltfBg = useGLTF(ASSET_PATHS.background, true);
  const gltfModelSb = useGLTF(ASSET_PATHS.characterSb, true);
  const gltfAnimSb = useGLTF(ASSET_PATHS.animationSb, true);
  const gltfAnimSbIntro = useGLTF(ASSET_PATHS.animationSbIntro, true);
  const gltfModelMary = useGLTF(ASSET_PATHS.characterMary, true);
  const gltfAnimMary = useGLTF(ASSET_PATHS.animationMary, true);
  const gltfAnimMaryIntro = useGLTF(ASSET_PATHS.animationMaryIntro);

  const mixerRefSb = React.useRef<THREE.AnimationMixer | null>(null);
  const mixerRefMary = React.useRef<THREE.AnimationMixer | null>(null);
  const introMixerRefSb = React.useRef<THREE.AnimationMixer | null>(null);
  const introMixerRefMary = React.useRef<THREE.AnimationMixer | null>(null);
  const introAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const introPlaybackStartedRef = React.useRef(false);
  const introFirstFrameAppliedRef = React.useRef(false);
  // True when the intro is being replayed from idle (characters already visible);
  // false on the very first play (coming from preIntro, characters must be hidden
  // until the intro mixer applies its first frame).
  const isReplayingRef = React.useRef(false);
  const [introCharactersVisible, setIntroCharactersVisible] = useState(
    () => introHasPlayed,
  );

  // Camera transition refs
  const cam2Ref = React.useRef<any>(null);
  const camTargetPos = React.useRef<THREE.Vector3 | null>(null);
  const camTargetQuat = React.useRef<THREE.Quaternion | null>(null);
  const camTargetFov = React.useRef<number | null>(null);
  const camTransitioning = React.useRef(false);
  // Snap on the very first camera placement; lerp on subsequent phase changes
  const camFirstSet = React.useRef(true);
  // Pre-stored Camera001 transform — used by the timed trigger before phase flips to idle
  const idleCamTargetPos = React.useRef<THREE.Vector3 | null>(null);
  const idleCamTargetQuat = React.useRef<THREE.Quaternion | null>(null);
  const idleCamTargetFov = React.useRef<number | null>(null);
  // One-shot gate so the timed camera trigger fires exactly once per intro
  const camTransitionTriggered = React.useRef(false);
  // Lerp speed for the current camera transition (exponential decay coefficient)
  const camLerpSpeed = React.useRef(1.5);
  // Wall-clock start of the intro (ms) — fallback when audio is autoplay-blocked
  const introStartTimeRef = React.useRef(0);

  // Lip sync throttle accumulators
  const sbLipSyncAcc = React.useRef(0);
  const maryLipSyncAcc = React.useRef(0);

  // Memoize cloned assets
  const { clonedScene, clonedModelSb, clonedModelMary } = useMemo(() => {
    if (!gltfBg?.scene || !gltfModelSb?.scene || !gltfModelMary?.scene) {
      return { clonedScene: null, clonedModelSb: null, clonedModelMary: null };
    }

    console.log("🔄 Cloning compressed assets...");
    const clonedScene = gltfBg.scene.clone();
    clonedScene.children.forEach((child: any) => {
      if (child.name === "Sun") child.intensity = 1.9; // Reduced from original to avoid fur blowout
    });

    let clonedModelSb: THREE.Group;
    try {
      clonedModelSb = SkeletonUtils.clone(gltfModelSb.scene) as THREE.Group;
      clonedModelSb.visible = true;
    } catch (error) {
      console.warn("SkeletonUtils failed, using regular clone:", error);
      clonedModelSb = gltfModelSb.scene.clone();
      clonedModelSb.visible = true;
    }

    let clonedModelMary: THREE.Group;
    try {
      clonedModelMary = SkeletonUtils.clone(gltfModelMary.scene) as THREE.Group;
      clonedModelMary.visible = true;
    } catch (error) {
      console.warn(
        "SkeletonUtils failed for Mary Bear, using regular clone:",
        error,
      );
      clonedModelMary = gltfModelMary.scene.clone();
      clonedModelMary.visible = true;
    }

    return { clonedScene, clonedModelSb, clonedModelMary };
  }, [gltfBg?.scene, gltfModelSb?.scene, gltfModelMary?.scene]);

  // Dismiss the Suspense spinner as soon as the background is ready
  useEffect(() => {
    if (clonedScene) setSuspenseLoading(false);
  }, [clonedScene, setSuspenseLoading]);

  // When language is selected, stop idle mixers and begin the intro
  useEffect(() => {
    if (!startIntro || phase !== "preIntro") return;
    mixerRefSb.current?.stopAllAction();
    mixerRefSb.current?.uncacheRoot(mixerRefSb.current.getRoot());
    mixerRefSb.current = null;
    mixerRefMary.current?.stopAllAction();
    mixerRefMary.current?.uncacheRoot(mixerRefMary.current.getRoot());
    mixerRefMary.current = null;
    setPhase("intro");
  }, [startIntro, phase]);

  // ── Intro animation (plays once per page load) ──────────────────────────────
  useEffect(() => {
    if (
      phase !== "intro" ||
      !startIntro ||
      !clonedModelSb ||
      !clonedModelMary ||
      !gltfAnimSbIntro?.animations[0] ||
      !gltfAnimMaryIntro?.animations[0]
    )
      return;

    console.log("🎬 Playing intro animations...");
    camTransitionTriggered.current = false;
    introPlaybackStartedRef.current = false;
    introFirstFrameAppliedRef.current = false;
    // Hide characters only on first play (preIntro→intro). On replay they are
    // already visible from idle; hiding them would cause a flash before the
    // intro mixer's first frame is applied.
    if (!isReplayingRef.current) {
      setIntroCharactersVisible(false);
    }
    isReplayingRef.current = false; // reset for next cycle

    // Spirit Bear intro
    const mixerSb = new THREE.AnimationMixer(clonedModelSb);
    const actionSb = mixerSb.clipAction(gltfAnimSbIntro.animations[0]);
    actionSb.setLoop(THREE.LoopOnce, 1);
    actionSb.clampWhenFinished = true;
    actionSb.play();
    introMixerRefSb.current = mixerSb;

    // Mary Bear intro
    const mixerMary = new THREE.AnimationMixer(clonedModelMary);
    const actionMary = mixerMary.clipAction(gltfAnimMaryIntro.animations[0]);
    actionMary.setLoop(THREE.LoopOnce, 1);
    actionMary.clampWhenFinished = true;
    actionMary.play();
    introMixerRefMary.current = mixerMary;

    // Transition to idle when Spirit Bear intro finishes
    const handleFinished = (e: any) => {
      if (e.action !== actionSb) return;
      mixerSb.removeEventListener("finished", handleFinished);

      // Null refs without calling stopAllAction so the clamped last-frame pose
      // is held until the idle mixer takes over, preventing a T-pose flash.
      introMixerRefSb.current = null;
      introMixerRefMary.current = null;

      // Stop intro audio
      if (introAudioRef.current) {
        introAudioRef.current.pause();
        introAudioRef.current = null;
      }

      introHasPlayed = true;
      onIntroComplete?.();
      setPhase("idle");
    };
    mixerSb.addEventListener("finished", handleFinished);

    // Intro audio — play immediately; catch rejection if autoplay is blocked
    const audio = new Audio(
      "/assets/audio/MainMenu/Intro_1B_MapTitleScreen.mp3",
    );
    const handleAudioPlaying = () => {
      introPlaybackStartedRef.current = true;
      introStartTimeRef.current = Date.now();
    };

    introAudioRef.current = audio;
    audio.addEventListener("playing", handleAudioPlaying, { once: true });
    audio.play().catch(() => {
      introPlaybackStartedRef.current = true;
      introStartTimeRef.current = Date.now();
      console.warn(
        "Intro audio blocked by autoplay policy — playing silently.",
      );
    });

    setSuspenseLoading(false);

    // Expose skip function so the UI can trigger it imperatively
    if (skipIntroRef) {
      skipIntroRef.current = () => {
        mixerSb.removeEventListener("finished", handleFinished);
        // Null refs without stopAllAction to avoid T-pose flash on skip too.
        introMixerRefSb.current = null;
        introMixerRefMary.current = null;
        if (introAudioRef.current) {
          introAudioRef.current.pause();
          introAudioRef.current = null;
        }
        introHasPlayed = true;
        onIntroComplete?.();
        setSuspenseLoading(false);
        // Lerp camera to Camera001 (idle position)
        camTargetPos.current = idleCamTargetPos.current;
        camTargetQuat.current = idleCamTargetQuat.current;
        camTargetFov.current = idleCamTargetFov.current;
        camTransitioning.current = true;
        setPhase("idle");
      };
    }

    return () => {
      if (skipIntroRef) skipIntroRef.current = null;
      mixerSb.removeEventListener("finished", handleFinished);
      audio.removeEventListener("playing", handleAudioPlaying);
      // Do NOT call stopAllAction or uncacheRoot here — both ultimately cause
      // bones to be written with stale/default values for one frame before the
      // idle mixer applies its first update, producing a visible flash.
      introMixerRefSb.current = null;
      introMixerRefMary.current = null;
      introPlaybackStartedRef.current = false;
      introFirstFrameAppliedRef.current = false;
      if (introAudioRef.current) {
        introAudioRef.current.pause();
        introAudioRef.current = null;
      }
    };
  }, [
    phase,
    clonedModelSb,
    clonedModelMary,
    gltfAnimSbIntro,
    gltfAnimMaryIntro,
    onIntroComplete,
    setSuspenseLoading,
  ]);

  // ── Idle animation (preIntro preview + loops after intro/on return visits) ──
  useEffect(() => {
    if (
      (phase !== "idle" && phase !== "preIntro") ||
      !clonedModelSb ||
      !gltfAnimSb?.animations[0]
    )
      return;

    console.log("🎬 Setting up Spirit Bear animation...");
    const mixerSb = new THREE.AnimationMixer(clonedModelSb);
    const actionSb = mixerSb.clipAction(gltfAnimSb.animations[0]);
    actionSb.setLoop(THREE.LoopRepeat, Infinity);
    actionSb.play();

    mixerRefSb.current = mixerSb;
    setIntroCharactersVisible(true);
    setIsReady(true);
    setSuspenseLoading(false);

    console.log("🎉 Optimized scene ready with compressed assets!");

    return () => {
      if (mixerSb) {
        mixerSb.stopAllAction();
        // Do NOT call uncacheRoot — it invokes restoreOriginalState() which
        // snaps bones to their bind pose for one frame before the next mixer
        // takes over. The mixer will be GC'd when this closure releases.
      }
    };
  }, [phase, clonedModelSb, gltfAnimSb, setSuspenseLoading]);

  useEffect(() => {
    if (
      (phase !== "idle" && phase !== "preIntro") ||
      !clonedModelMary ||
      !gltfAnimMary?.animations[0]
    )
      return;

    console.log("🎬 Setting up Mary Bear animation...");
    const mixerMary = new THREE.AnimationMixer(clonedModelMary);
    const actionMary = mixerMary.clipAction(gltfAnimMary.animations[0]);
    actionMary.setLoop(THREE.LoopRepeat, Infinity);
    actionMary.play();

    mixerRefMary.current = mixerMary;

    return () => {
      if (mixerMary) {
        mixerMary.stopAllAction();
        // Do NOT call uncacheRoot — same reason as the Spirit Bear mixer above.
      }
    };
  }, [phase, clonedModelMary, gltfAnimMary]);

  // Animation loop
  useFrame((state, delta) => {
    const shouldAdvanceIntro =
      phase !== "intro" || introPlaybackStartedRef.current;

    if (shouldAdvanceIntro) {
      introMixerRefSb.current?.update(delta);
      introMixerRefMary.current?.update(delta);

      if (phase === "intro" && !introFirstFrameAppliedRef.current) {
        introFirstFrameAppliedRef.current = true;
        setIntroCharactersVisible(true);
      }
    }
    mixerRefSb.current?.update(delta);
    mixerRefMary.current?.update(delta);

    // Audio-timed camera trigger — fires once when playback crosses the threshold
    if (
      phase === "intro" &&
      !camTransitionTriggered.current &&
      introStartTimeRef.current > 0
    ) {
      const audioTime = introAudioRef.current?.currentTime ?? 0;
      const wallTime = (Date.now() - introStartTimeRef.current) / 1000;
      // Use actual audio position; fall back to wall clock when autoplay is blocked
      const effectiveTime = audioTime > 0 ? audioTime : wallTime;
      if (effectiveTime >= INTRO_CAM_TRANSITION_AT) {
        camTargetPos.current = idleCamTargetPos.current;
        camTargetQuat.current = idleCamTargetQuat.current;
        camTargetFov.current = idleCamTargetFov.current;
        camLerpSpeed.current = 0.5;
        camTransitioning.current = true;
        camTransitionTriggered.current = true;
      }
    }

    // Camera lerp transition
    if (
      camTransitioning.current &&
      camTargetPos.current &&
      camTargetQuat.current &&
      camTargetFov.current !== null
    ) {
      const t = 1 - Math.exp(-camLerpSpeed.current * delta);
      const cam = camera as THREE.PerspectiveCamera;
      camera.position.lerp(camTargetPos.current, t);
      camera.quaternion.slerp(camTargetQuat.current, t);
      cam.fov += (camTargetFov.current - cam.fov) * t;
      cam.updateProjectionMatrix();

      // Settle: snap when close enough
      if (camera.position.distanceTo(camTargetPos.current) < 0.001) {
        camera.position.copy(camTargetPos.current);
        camera.quaternion.copy(camTargetQuat.current);
        cam.fov = camTargetFov.current;
        cam.updateProjectionMatrix();
        camTransitioning.current = false;
      }
    }

    // Wave animation (reduced frequency for performance)
    // @ts-ignore
    if (gltfBg?.materials?.shore_ripples?.map?.offset) {
      // @ts-ignore
      const ripples = gltfBg.materials.shore_ripples.map.offset;
      ripples.y = (ripples.y + 0.0005) % 1;
    }

    // Spirit Bear lip sync
    if (clonedModelSb) {
      sbLipSyncAcc.current += delta;
      if (sbLipSyncAcc.current >= 1 / LIPSYNC_FPS) {
        sbLipSyncAcc.current = 0;
        const phonemePicker = clonedModelSb.getObjectByName("phoneme_picker");
        const emotionPicker = clonedModelSb.getObjectByName("emotion_picker");
        if (phonemePicker && emotionPicker) {
          let euler = phonemePicker.rotation.z * MathUtils.RAD2DEG;
          let x =
            (mapLinear(Math.round(euler / 36) * 36, -180, 180, 0, 10) % 10) *
            36;
          x = x === 324 ? 0 : x;

          let euler_emotion = emotionPicker.rotation.z * MathUtils.RAD2DEG;
          let w =
            (mapLinear(Math.round(euler_emotion / 90) * 90, -180, 180, 0, 4) %
              4) *
            90;
          w = w > 90 ? 0 : w;

          // @ts-ignore
          const mouthSb = gltfModelSb.materials?.spirit_bear_mouth as
            | THREE.MeshStandardMaterial
            | undefined;
          if (mouthSb?.map) {
            const uvX = (x % 180) / 180;
            const m = x > 180 ? 0.2 : 0;
            let uvY = m + (w / 90) * 0.4;
            mouthSb.map.offset.set(uvX, uvY);
          }
        }
      }
    }

    // Mary Bear lip sync
    if (clonedModelMary) {
      maryLipSyncAcc.current += delta;
      if (maryLipSyncAcc.current >= 1 / LIPSYNC_FPS) {
        maryLipSyncAcc.current = 0;
        const phonemePicker = clonedModelMary.getObjectByName("phoneme_picker");
        const emotionPicker = clonedModelMary.getObjectByName("emotion_picker");
        if (phonemePicker && emotionPicker) {
          let euler = phonemePicker.rotation.z * MathUtils.RAD2DEG;
          let x =
            (mapLinear(Math.round(euler / 36) * 36, -180, 180, 0, 10) % 10) *
            36;
          x = x === 324 ? 0 : x;

          let euler_emotion = emotionPicker.rotation.z * MathUtils.RAD2DEG;
          let w =
            (mapLinear(Math.round(euler_emotion / 90) * 90, -180, 180, 0, 4) %
              4) *
            90;
          w = w > 90 ? 0 : w;

          // @ts-ignore
          const mouthMary = gltfModelMary.materials?.spirit_bear_mouth as
            | THREE.MeshStandardMaterial
            | undefined;
          if (mouthMary?.map) {
            const uvX = (x % 180) / 180;
            const m = x > 180 ? 0.2 : 0;
            let uvY = m + (w / 90) * 0.4;
            mouthMary.map.offset.set(uvX, uvY);
          }
        }
      }
    }
  });

  // Camera setup — pre-store Camera001 target for the audio-timed transition.
  // On first mount only, snap the live camera to the correct starting position.
  // All subsequent movement is driven entirely by the timed trigger in useFrame.
  const { camera } = useThree();
  useEffect(() => {
    if (!clonedScene) return;

    let cam1: any = null;
    let cam2: any = null;
    clonedScene.children.forEach((child: any) => {
      if (child.name === "Camera001") cam1 = child;
      if (child.name === "Camera002") cam2 = child;
    });

    // Always pre-store Camera001 so the timed trigger can point to it during the intro
    if (cam1) {
      idleCamTargetPos.current = cam1.position.clone();
      idleCamTargetQuat.current = cam1.quaternion.clone();
      idleCamTargetFov.current = cam1.fov;
    }

    cam2Ref.current = cam2;

    // Snap to Camera001 on first mount so the scene is visible behind the StartModal
    if (camFirstSet.current) {
      const snapCam = cam1;
      if (snapCam) {
        camTargetPos.current = snapCam.position.clone();
        camTargetQuat.current = snapCam.quaternion.clone();
        camTargetFov.current = snapCam.fov;
        camera.position.copy(snapCam.position);
        camera.quaternion.copy(snapCam.quaternion);
        (camera as THREE.PerspectiveCamera).fov = snapCam.fov;
        camera.updateProjectionMatrix();
        camFirstSet.current = false;
      }
    }
  }, [camera, clonedScene, phase]);

  // Expose a replay function so the UI can restart the intro imperatively
  useEffect(() => {
    if (!replayIntroRef) return;
    replayIntroRef.current = () => {
      // stopAllAction is safe — it deactivates actions without resetting bone
      // transforms, so the idle pose is held until the intro mixer takes over.
      // Do NOT call uncacheRoot here: it calls restoreOriginalState() internally
      // which immediately snaps bones to their bind pose, causing a visible flash.
      mixerRefSb.current?.stopAllAction();
      mixerRefSb.current = null;
      mixerRefMary.current?.stopAllAction();
      mixerRefMary.current = null;
      introHasPlayed = false;
      isReplayingRef.current = true; // coming from idle, characters already visible
      // Trigger camera lerp to Camera002 synchronously here rather than relying
      // on the useEffect — startIntro never re-flips on replay so the effect
      // only re-runs when phase changes, which can race with cam2Ref being ready.
      camTransitionTriggered.current = false;
      if (cam2Ref.current) {
        camTargetPos.current = cam2Ref.current.position.clone();
        camTargetQuat.current = cam2Ref.current.quaternion.clone();
        camTargetFov.current = cam2Ref.current.fov;
        camTransitioning.current = true;
      } else {
        console.warn(
          "replayIntro: cam2Ref is null, camera will not move to intro position",
        );
      }
      setPhase("intro");
    };
    return () => {
      replayIntroRef.current = null;
    };
  }, [replayIntroRef]);

  // When startIntro flips to true, lerp camera to Camera002 (intro starting position)
  useEffect(() => {
    if (!startIntro || phase !== "intro" || !cam2Ref.current) return;
    const cam2 = cam2Ref.current;
    camTargetPos.current = cam2.position.clone();
    camTargetQuat.current = cam2.quaternion.clone();
    camTargetFov.current = cam2.fov;
    camTransitioning.current = true;
  }, [startIntro, phase]);

  // Scene labels
  const sceneLabels = useMemo(() => {
    if (!clonedScene || !isReady || phase === "intro") return null;

    const elements: JSX.Element[] = [];
    clonedScene.children.forEach((child: any) => {
      if (child.name.includes("Object")) {
        elements.push(
          <group
            key={`${child.name}-${lang}`}
            position={[child.position.x, child.position.y, child.position.z]}
          >
            <Html center zIndexRange={[10, 0]}>
              <div
                className={
                  "SceneName " + sceneKeys[`${child.name}_scene`] + ` ${lang}`
                }
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  setScene(sceneKeys[`${child.name}_scene`]);
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  setScene(sceneKeys[`${child.name}_scene`]);
                }}
              >
                <p>{t(`${child.name}_label`)}</p>
                <div
                  className={
                    "SceneNameHitBox " + sceneKeys[`${child.name}_scene`]
                  }
                ></div>
              </div>
            </Html>
          </group>,
        );
      }
    });
    return elements;
  }, [clonedScene, isReady, phase, setScene, lang]);

  return (
    <>
      {/* Neutral ambient fill — avoids colour tinting fur */}
      <ambientLight intensity={0.6} />

      {clonedScene && <primitive object={clonedScene}>{sceneLabels}</primitive>}
      {(introCharactersVisible || phase === "preIntro") && clonedModelSb && (
        <primitive object={clonedModelSb} dispose={null} />
      )}
      {(introCharactersVisible || phase === "preIntro") && clonedModelMary && (
        <primitive object={clonedModelMary} dispose={null} />
      )}
    </>
  );
}

export default Main;
